import axios from 'axios';
import classNames from 'classnames';
import React from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useIsExternal } from '@/utils/hooks/useUserType';
import { ProjectService } from '@/api/project';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import { ExternalAccessBanner } from '@/components/shared/ExternalAccessBanner';
import { Loading } from '@/components/Loading';

export const DetailProjectLayout: React.FC = () => {
  const location = useLocation();
  const { id, testRunId } = useParams();
  const isExternal = useIsExternal();
  const { isError, error, isLoading } = ProjectService.useGet(id, { retry: false });
  const path = location.pathname.split('/').pop();
  const isDetailTestRun = location.pathname.includes('test-run') && path !== 'test-run';

  const subMenu = [
    { link: '', label: 'Overview' },
    { link: 'module', label: 'Epic & User Story' },
    { link: 'test-case', label: 'Test Case' },
    { link: 'release', label: 'Release' },
    {
      link: isDetailTestRun ? `test-run/${testRunId}` : 'test-run',
      label: isDetailTestRun ? 'Detail Test Run' : 'Test Run',
    },
    { link: 'test-issue', label: 'Issue' },
    { link: 'test-plan', label: 'Test Plan' },
  ].filter(
    menu => !isExternal || !['release', 'test-plan'].includes(menu.link),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loading />
      </div>
    );
  }

  if (isError) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;

    if (status === 404) {
      return (
        <UnauthorizedPage
          statusCode="404"
          title="Not Found"
          message="Project not found or you are not assigned to this project."
        />
      );
    }

    if (status === 403) {
      return (
        <UnauthorizedPage
          statusCode="403"
          title="Forbidden"
          message="You do not have permission to access this project."
        />
      );
    }

    if (status === 401) {
      return (
        <UnauthorizedPage
          statusCode="401"
          title="Unauthorized"
          message="You are not authorized to view this page. Please log in."
        />
      );
    }

    const errorMessage =
      (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) ||
      'An unexpected error occurred while loading project data.';

    return (
      <UnauthorizedPage
        statusCode={status || '500'}
        title={status === 500 ? 'Internal Server Error' : 'Error'}
        message={errorMessage}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {isExternal && (
        <div className="mx-4">
          <ExternalAccessBanner />
        </div>
      )}
      <div className="flex flex-row gap-4 p-3 mx-4 bg-white rounded-md shadow-md">
        {subMenu.map(menu => {
          const isActive =
            (menu.link === '' && location.pathname === `/project/${testRunId}`) || // Hanya aktif di halaman Overview jika path persis seperti project ID
            (menu.link !== '' && location.pathname.endsWith(menu.link)) || // Cocokkan menu selain Overview dengan path terakhir
            (isDetailTestRun && menu.link.includes('test-run') && location.pathname.includes(menu.link)); // Kondisi khusus untuk Detail Test Run

          return (
            <Link
              key={menu.link}
              to={menu.link}
              className={classNames(
                'text-sm text-gray-500 hover:text-black hover:underline underline-offset-4',
                {
                  '!text-black underline': isActive,
                }
              )}
            >
              {menu.label}
            </Link>
          );
        })}
      </div>

      <Outlet />
    </div>
  );
};
